import { useEffect, useState, type ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  CLASSIFICATION_GROUNDING_TAGS,
  CLASSIFICATION_LATERALIZATION_TAGS,
  CLASSIFICATION_ORIENTATION_TAGS,
  CLASSIFICATION_PHASE_TAGS,
  getClassificationTagColor,
  type ClassificationTagId,
} from '@/lib/clients/classification-tags';
import type { ClientNeedsTag } from '@/lib/types';
import { needsTagKey } from '@/lib/clients/needs-tags';
import { loadColors, getTagColor, colorIsLight } from '@/utils/drill-ui';
import { cn } from '@/lib/utils';
import { Filter, X } from 'lucide-react';

interface ClientClassificationFilterPanelProps {
  selected: ClassificationTagId[];
  classificationFilterCount: number;
  needsFilters?: string[];
  needsOptions?: ClientNeedsTag[];
  onToggle: (tagId: ClassificationTagId) => void;
  onToggleNeedsFilter?: (needsKey: string) => void;
  onClearAll: () => void;
}

function ClassificationFilterChip({
  tagId,
  selected,
  onToggle,
}: {
  tagId: ClassificationTagId;
  selected: boolean;
  onToggle: (tagId: ClassificationTagId) => void;
}) {
  const color = getClassificationTagColor(tagId);
  const textColorClass = colorIsLight(color) ? 'text-foreground' : 'text-white';

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onToggle(tagId)}
      className={cn(
        'inline-flex h-8 min-w-[3rem] items-center justify-center rounded-full px-3 text-xs font-semibold',
        'transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        textColorClass,
        selected
          ? 'ring-2 ring-primary ring-offset-2 ring-offset-popover shadow-sm scale-[1.02]'
          : 'opacity-70 hover:opacity-100 hover:shadow-sm'
      )}
      style={{ backgroundColor: color }}
    >
      {tagId}
    </button>
  );
}

function FilterSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">{title}</h3>
        {description && (
          <p className="text-[11px] text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </section>
  );
}

/** Compact filter body — used inside the toolbar popover. */
export function ClientClassificationFilterPanel({
  selected,
  classificationFilterCount,
  needsFilters = [],
  needsOptions = [],
  onToggle,
  onToggleNeedsFilter,
  onClearAll,
}: ClientClassificationFilterPanelProps) {
  const totalActive = classificationFilterCount + needsFilters.length;

  const [colorsReady, setColorsReady] = useState(false);
  useEffect(() => {
    let active = true;
    void loadColors().finally(() => {
      if (active) setColorsReady(true);
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="flex flex-col" data-testid="classification-filter-panel">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Filter className="h-4 w-4 text-primary" aria-hidden />
          Classification filters
          {totalActive > 0 && (
            <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
              {totalActive}
            </Badge>
          )}
        </div>
        {totalActive > 0 && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            onClick={onClearAll}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Clear all
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4 px-4 py-4 max-h-[min(70vh,24rem)] overflow-y-auto">
        <FilterSection title="Orientation" description="Pelvic pattern">
          {CLASSIFICATION_ORIENTATION_TAGS.map((tag) => (
            <ClassificationFilterChip
              key={tag}
              tagId={tag}
              selected={selected.includes(tag)}
              onToggle={onToggle}
            />
          ))}
        </FilterSection>

        <FilterSection title="Lateralization">
          {CLASSIFICATION_LATERALIZATION_TAGS.map((tag) => (
            <ClassificationFilterChip
              key={tag}
              tagId={tag}
              selected={selected.includes(tag)}
              onToggle={onToggle}
            />
          ))}
        </FilterSection>

        <FilterSection title="Grounding">
          {CLASSIFICATION_GROUNDING_TAGS.map((tag) => (
            <ClassificationFilterChip
              key={tag}
              tagId={tag}
              selected={selected.includes(tag)}
              onToggle={onToggle}
            />
          ))}
        </FilterSection>

        <FilterSection title="Phase">
          {CLASSIFICATION_PHASE_TAGS.map((tag) => (
            <ClassificationFilterChip
              key={tag}
              tagId={tag}
              selected={selected.includes(tag)}
              onToggle={onToggle}
            />
          ))}
        </FilterSection>

        {needsOptions.length > 0 && onToggleNeedsFilter && (
          <FilterSection title="Needs" description="Drill-library tags assigned to clients">
            {needsOptions.map((tag) => {
              const key = needsTagKey(tag);
              const active = needsFilters.includes(key);
              const color = colorsReady ? getTagColor(tag.category, tag.value) : '#64748B';
              const textColorClass = colorIsLight(color) ? 'text-foreground' : 'text-white';
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onToggleNeedsFilter(key)}
                  title={`${tag.category}: ${tag.value}`}
                  className={cn(
                    'inline-flex h-8 items-center rounded-full px-3 text-xs font-medium transition-all',
                    textColorClass,
                    active
                      ? 'ring-2 ring-primary ring-offset-2 ring-offset-popover shadow-sm scale-[1.02]'
                      : 'opacity-70 hover:opacity-100 hover:shadow-sm'
                  )}
                  style={{ backgroundColor: color }}
                >
                  {tag.value}
                </button>
              );
            })}
          </FilterSection>
        )}
      </div>
    </div>
  );
}
